import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { motion } from "framer-motion";
import placeholderImage from "../image/project_Image_home.webp";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

const baseImageBucketUrl = process.env.REACT_APP_S3_IMAGE_BUCKET_URL;

const getPropertyImage = (property) =>
  property.projectImage
    ? `${baseImageBucketUrl}${property.projectImage}`
    : placeholderImage;

const getPropertyLocation = (property) =>
  property.location || property.city || "Homely developments";

export const PropertyPromise = ({ data, loading }) => {
  const history = useHistory();
  const featuredProperty = data?.[0];
  if (!loading && !featuredProperty) return null;

  return (
    <Box
      component="section"
      id="our-promise"
      aria-labelledby="our-promise-title"
      sx={{
        mt: { xs: 5, md: 8 },
        px: { xs: 2, sm: 3, md: 5 },
        pt: 0,
        pb: 0,
        backgroundColor: "#f6f4ef",
        scrollMarginTop: { xs: "60px", sm: "68px" },
      }}
    >
      <Box
        component={motion.div}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          mb: { xs: 4, md: 6 },
        }}
      >
        <Typography
          component={motion.p}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          sx={{
            mb: 1.5,
            color: "#9a794b",
            fontSize: "0.7rem",
            fontWeight: 600,
            letterSpacing: "0.2em",
          }}
        >
          OUR PROMISE
        </Typography>
        <Typography
          id="our-promise-title"
          component="h2"
          variant="h4"
          sx={{
            maxWidth: 720,
            fontSize: { xs: "2rem", md: "2.8rem" },
            lineHeight: 1.2,
          }}
        >
          A more thoughtful way to come home
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.08fr 0.92fr" },
          maxWidth: 1440,
          minHeight: { md: 510 },
          mx: "auto",
          backgroundColor: "#eeece5",
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, x: -36 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          sx={{
            position: "relative",
            minHeight: { xs: 300, sm: 380, md: 510 },
            overflow: "hidden",
            backgroundColor: "#dedbd2",
          }}
        >
          {featuredProperty && (
            <>
              <Box
                component="img"
                src={getPropertyImage(featuredProperty)}
                alt={featuredProperty.projectTitle}
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transition: "transform 900ms ease",
                  "&:hover": { transform: "scale(1.025)" },
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(0deg, rgba(25, 24, 21, 0.52), transparent 42%)",
                }}
              />
              <Typography
                sx={{
                  position: "absolute",
                  left: { xs: 3, md: 5 },
                  bottom: { xs: 3, md: 5 },
                  color: "white",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                {getPropertyLocation(featuredProperty)}
              </Typography>
            </>
          )}
        </Box>

        <Box
          component={motion.div}
          initial={{ opacity: 0, x: 36 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1],
            delay: 0.12,
          }}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            px: { xs: 3, sm: 5, md: "6vw" },
            py: { xs: 5, md: 8 },
            minHeight: { md: 510 },
          }}
        >
          {loading ? (
            <Box
              aria-label="Loading featured development"
              sx={{
                width: "100%",
                height: { xs: 180, md: 220 },
                backgroundColor: "rgba(41, 39, 32, 0.08)",
              }}
            />
          ) : (
            <>
              <Typography
                component="p"
                sx={{
                  mb: 2,
                  color: "#9a794b",
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                }}
              >
                {featuredProperty.status || "A HOMELY DEVELOPMENT"}
              </Typography>
              <Typography
                component="h3"
                variant="h3"
                sx={{
                  maxWidth: 500,
                  fontSize: { xs: "2.35rem", md: "3.25rem" },
                  lineHeight: 1.1,
                }}
              >
                {featuredProperty.projectTitle}
              </Typography>
              <Typography
                sx={{
                  mt: 2.5,
                  maxWidth: 520,
                  color: "text.secondary",
                  fontSize: "0.94rem",
                  lineHeight: 1.85,
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 4,
                  overflow: "hidden",
                }}
              >
                {featuredProperty.projectInfo ||
                  `Discover ${featuredProperty.projectTitle}, a Homely development in ${getPropertyLocation(
                    featuredProperty
                  )}.`}
              </Typography>
              {featuredProperty.displayDetailsPageFlag && (
                <Button
                  variant="outlined"
                  onClick={() =>
                    history.push(`/real-estate/${featuredProperty.id}`, {
                      property: featuredProperty,
                      loading,
                    })
                  }
                  sx={{
                    mt: 3.5,
                    px: 3,
                    py: 1.4,
                    borderColor: "#292720",
                    borderRadius: 0,
                    color: "#292720",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    "&:hover": {
                      borderColor: "#292720",
                      backgroundColor: "rgba(41, 39, 32, 0.06)",
                    },
                  }}
                >
                  Discover more
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          maxWidth: 1440,
          mx: "auto",
          backgroundColor: "#292720",
          color: "#f6f4ef",
        }}
      >
        {[
          ["01", "Thoughtful design"],
          ["02", "Enduring craftsmanship"],
          ["03", "Made for living"],
        ].map(([number, value], index) => (
          <Box
            key={number}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: { xs: 3, md: 4 },
              py: { xs: 2.5, md: 3 },
              borderRight: {
                sm: index < 2 ? "1px solid rgba(246,244,239,0.18)" : "none",
              },
              borderBottom: {
                xs: index < 2 ? "1px solid rgba(246,244,239,0.18)" : "none",
                sm: "none",
              },
            }}
          >
            <Typography
              component="span"
              sx={{
                color: "#d5bd91",
                fontSize: "0.66rem",
                fontWeight: 600,
                letterSpacing: "0.12em",
              }}
            >
              {number}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: { xs: "1rem", md: "1.08rem" },
              }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const Fmain = ({ data, loading }) => {
  const history = useHistory();
  const swiperRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const hasSingleProperty = data?.length === 1;

  return (
    <Box
      component="section"
      id="our-presence"
      aria-labelledby="our-presence-title"
      sx={{
        pt: { xs: 7, md: 10 },
        pb: { xs: 3, md: 4 },
        backgroundColor: "#f6f4ef",
      }}
    >
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.65, ease: "easeOut" }}
        sx={{
          px: { xs: 3, md: 8 },
          mb: { xs: 3, md: 5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          textAlign: "center",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography
            component="p"
            sx={{
              mb: 1.5,
              color: "#9a794b",
              fontSize: "0.7rem",
              fontWeight: 600,
              letterSpacing: "0.2em",
            }}
          >
            HOMELY DEVELOPMENTS
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 2, md: 3 } }}>
            <Typography
              id="our-presence-title"
              component="h2"
              variant="h3"
              sx={{ fontSize: { xs: "2.4rem", md: "3.3rem" } }}
            >
              Featured Properties
            </Typography>
          </Box>
        </Box>
        <Typography
          sx={{
            maxWidth: 520,
            color: "text.secondary",
            fontSize: "0.9rem",
            lineHeight: 1.7,
            textAlign: "center",
          }}
        >
          Explore thoughtfully created places to live, work, and belong.
        </Typography>
      </Box>

      {loading ? (
        <Typography sx={{ px: { xs: 3, md: 8 }, color: "text.secondary" }}>
          Loading developments…
        </Typography>
      ) : !data?.length ? (
        <Box
          sx={{
            mx: { xs: 3, md: 8 },
            px: { xs: 3, md: 5 },
            py: { xs: 4, md: 5 },
            borderTop: "1px solid #d9d5ca",
            borderBottom: "1px solid #d9d5ca",
            color: "text.secondary",
          }}
        >
          <Typography>No developments are available at the moment.</Typography>
        </Box>
      ) : (
        <Box sx={{ maxWidth: hasSingleProperty ? 1120 : "none", mx: "auto" }}>
          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
              setActiveSlide(swiper.realIndex);
            }}
            onSlideChange={(swiper) => setActiveSlide(swiper.realIndex)}
            spaceBetween={24}
            slidesPerView={hasSingleProperty ? 1 : "auto"}
            centeredSlides={!hasSingleProperty}
            loop={data.length > 3}
            rewind={data.length > 1 && data.length <= 3}
            autoplay={
              data.length > 1
                ? { delay: 5500, disableOnInteraction: false }
                : false
            }
            modules={[Autoplay]}
            style={{
              margin: "0 auto",
              padding: hasSingleProperty ? "0 24px 12px" : "0 8px 18px",
            }}
          >
            {data.map((property, index) => {
              const canViewDetails = Boolean(property.displayDetailsPageFlag);

              return (
                <SwiperSlide
                  key={property.id || `${property.projectTitle}-${index}`}
                  style={{
                    width: hasSingleProperty ? "100%" : "min(82vw, 380px)",
                    height: "auto",
                  }}
                >
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{
                    duration: 0.65,
                    ease: "easeOut",
                    delay: Math.min(index * 0.1, 0.3),
                  }}
                  sx={{ height: "100%", pb: 1 }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      height: hasSingleProperty
                        ? { xs: "auto", md: "auto" }
                        : { xs: 330, md: 400 },
                      aspectRatio: hasSingleProperty
                        ? { xs: "4 / 3", md: "2.05 / 1" }
                        : "auto",
                      overflow: "hidden",
                      backgroundColor: "#dedbd2",
                    }}
                  >
                    <Box
                      component="img"
                      src={getPropertyImage(property)}
                      alt={property.projectTitle}
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "block",
                        objectFit: "cover",
                        transition: "transform 700ms ease",
                        "&:hover": { transform: "scale(1.04)" },
                      }}
                    />
                    {property.status && (
                      <Typography
                        sx={{
                          position: "absolute",
                          left: 2.5,
                          bottom: 2.5,
                          color: "white",
                          fontSize: "0.66rem",
                          fontWeight: 600,
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          textShadow: "0 1px 8px rgba(0,0,0,0.6)",
                        }}
                      >
                        {property.status}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      pt: { xs: 2, md: 2.5 },
                      minHeight: { xs: 82, md: 92 },
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) auto",
                      alignItems: "center",
                      justifyContent: "space-between",
                      columnGap: { xs: 1.5, md: 3 },
                      textAlign: "left",
                    }}
                  >
                    <Box>
                      <Typography
                        component="p"
                        sx={{
                          mb: 0.7,
                          maxWidth: { xs: "34ch", md: "60ch" },
                          color: "#9a794b",
                          fontSize: { xs: "0.62rem", md: "0.68rem" },
                          fontWeight: 600,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          lineHeight: 1.6,
                        }}
                      >
                        {getPropertyLocation(property)}
                      </Typography>
                      <Typography
                        component="h3"
                        sx={{
                          color: "#292720",
                          fontFamily: 'Georgia, "Times New Roman", serif',
                          fontSize: { xs: "1.3rem", md: "1.65rem" },
                          lineHeight: 1.2,
                        }}
                      >
                        {property.projectTitle}
                      </Typography>
                    </Box>
                    {canViewDetails && (
                      <Button
                        onClick={() =>
                          history.push(`/real-estate/${property.id}`, {
                            property,
                            loading,
                          })
                        }
                        sx={{
                          flexShrink: 0,
                          minWidth: 0,
                          px: { xs: 1, md: 1.5 },
                          py: 1,
                          justifySelf: "end",
                          alignSelf: "center",
                          color: "#292720",
                          fontSize: { xs: "0.62rem", md: "0.68rem" },
                          fontWeight: 600,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                          "&:hover": {
                            backgroundColor: "transparent",
                            color: "#9a794b",
                          },
                        }}
                      >
                        View details
                      </Button>
                    )}
                  </Box>
                </Box>
                </SwiperSlide>
              );
            })}
          </Swiper>
          {data.length > 1 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mx: { xs: 3, md: 8 },
                pt: 1.5,
                borderTop: "1px solid #d9d5ca",
              }}
            >
              <Typography
                aria-live="polite"
                sx={{
                  color: "#706d64",
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                }}
              >
                {String(activeSlide + 1).padStart(2, "0")}{" "}
                <Box component="span" sx={{ color: "#a68757" }}>
                  /
                </Box>{" "}
                {String(data.length).padStart(2, "0")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                  aria-label="Previous development"
                  onClick={() => swiperRef.current?.slidePrev()}
                  sx={developmentArrowStyles}
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
                </IconButton>
                <IconButton
                  aria-label="Next development"
                  onClick={() => swiperRef.current?.slideNext()}
                  sx={developmentArrowStyles}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const developmentArrowStyles = {
  width: 40,
  height: 40,
  color: "#292720",
  border: "1px solid #c9c4b8",
  borderRadius: 0,
  transition: "color 180ms ease, border-color 180ms ease, background-color 180ms ease",
  "&:hover": {
    color: "#f6f4ef",
    borderColor: "#292720",
    backgroundColor: "#292720",
  },
};

export default Fmain;
